/**
 * TriageStep Component
 * Step 2 of Quick Attendance Wizard - Vital signs collection (optional)
 *
 * @version 2025.1.0
 */

import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid,
  Button,
  InputAdornment,
  Divider,
  Alert,
} from '@mui/material';
import { SkipNext as SkipIcon } from '@mui/icons-material';
import { TriageStepProps } from '../QuickAttendanceWizard.types';
import { TriageData, TRIAGE_VALIDATION, calculateBMI, getBMIClassification } from '../../../../../types/triage';

/**
 * TriageStep - Second step of the wizard
 * Collects optional vital signs from patient
 */
export const TriageStep: React.FC<TriageStepProps> = ({
  triageData,
  onTriageDataChange,
  onSkip,
}) => {
  const handleFieldChange = (field: keyof TriageData, value: string) => {
    const numericFields = [
      'bloodPressureSystolic',
      'bloodPressureDiastolic',
      'heartRate',
      'temperature',
      'weight',
      'height',
      'oxygenSaturation',
    ];

    if (numericFields.includes(field)) {
      const numValue = value === '' ? null : parseFloat(value);
      onTriageDataChange({ ...triageData, [field]: numValue });
    } else {
      onTriageDataChange({ ...triageData, [field]: value });
    }
  };

  const getFieldError = (field: keyof typeof TRIAGE_VALIDATION, value: number | null): string | null => {
    if (value === null) return null;
    const validation = TRIAGE_VALIDATION[field];
    if (value < validation.min || value > validation.max) {
      return `Valor deve estar entre ${validation.min} e ${validation.max}`;
    }
    return null;
  };

  const bmi = calculateBMI(triageData.weight, triageData.height);
  const bmiClassification = getBMIClassification(bmi);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="body2" color="text.secondary">
          Preencha os sinais vitais do paciente (opcional):
        </Typography>
        <Button
          size="small"
          startIcon={<SkipIcon />}
          onClick={onSkip}
          color="inherit"
        >
          Pular Triagem
        </Button>
      </Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Todos os campos são opcionais. Preencha apenas o que for necessário.
      </Alert>
      <Grid container spacing={2}>
        {/* Blood Pressure */}
        <Grid size={12}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Pressão Arterial
          </Typography>
        </Grid>
        <Grid size={6}>
          <TextField
            fullWidth
            size="small"
            label="Sistólica"
            type="number"
            value={triageData.bloodPressureSystolic ?? ''}
            onChange={(e) => handleFieldChange('bloodPressureSystolic', e.target.value)}
            error={!!getFieldError('bloodPressureSystolic', triageData.bloodPressureSystolic)}
            helperText={getFieldError('bloodPressureSystolic', triageData.bloodPressureSystolic)}
            InputProps={{
              endAdornment: <InputAdornment position="end">mmHg</InputAdornment>,
            }}
            inputProps={{ min: 60, max: 250 }}
          />
        </Grid>
        <Grid size={6}>
          <TextField
            fullWidth
            size="small"
            label="Diastólica"
            type="number"
            value={triageData.bloodPressureDiastolic ?? ''}
            onChange={(e) => handleFieldChange('bloodPressureDiastolic', e.target.value)}
            error={!!getFieldError('bloodPressureDiastolic', triageData.bloodPressureDiastolic)}
            helperText={getFieldError('bloodPressureDiastolic', triageData.bloodPressureDiastolic)}
            InputProps={{
              endAdornment: <InputAdornment position="end">mmHg</InputAdornment>,
            }}
            inputProps={{ min: 40, max: 150 }}
          />
        </Grid>

        <Grid size={12}>
          <Divider sx={{ my: 1 }} />
        </Grid>

        {/* Heart Rate and Temperature */}
        <Grid size={6}>
          <TextField
            fullWidth
            size="small"
            label="Frequência Cardíaca"
            type="number"
            value={triageData.heartRate ?? ''}
            onChange={(e) => handleFieldChange('heartRate', e.target.value)}
            error={!!getFieldError('heartRate', triageData.heartRate)}
            helperText={getFieldError('heartRate', triageData.heartRate)}
            InputProps={{
              endAdornment: <InputAdornment position="end">bpm</InputAdornment>,
            }}
            inputProps={{ min: 30, max: 220 }}
          />
        </Grid>
        <Grid size={6}>
          <TextField
            fullWidth
            size="small"
            label="Temperatura"
            type="number"
            value={triageData.temperature ?? ''}
            onChange={(e) => handleFieldChange('temperature', e.target.value)}
            error={!!getFieldError('temperature', triageData.temperature)}
            helperText={getFieldError('temperature', triageData.temperature)}
            InputProps={{
              endAdornment: <InputAdornment position="end">°C</InputAdornment>,
            }}
            inputProps={{ min: 32, max: 42, step: 0.1 }}
          />
        </Grid>

        {/* Oxygen Saturation */}
        <Grid size={6}>
          <TextField
            fullWidth
            size="small"
            label="Saturação O2"
            type="number"
            value={triageData.oxygenSaturation ?? ''}
            onChange={(e) => handleFieldChange('oxygenSaturation', e.target.value)}
            error={!!getFieldError('oxygenSaturation', triageData.oxygenSaturation)}
            helperText={getFieldError('oxygenSaturation', triageData.oxygenSaturation)}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            inputProps={{ min: 70, max: 100 }}
          />
        </Grid>

        <Grid size={12}>
          <Divider sx={{ my: 1 }} />
        </Grid>

        {/* Weight and Height */}
        <Grid size={12}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Medidas Antropometricas
          </Typography>
        </Grid>
        <Grid size={4}>
          <TextField
            fullWidth
            size="small"
            label="Peso"
            type="number"
            value={triageData.weight ?? ''}
            onChange={(e) => handleFieldChange('weight', e.target.value)}
            error={!!getFieldError('weight', triageData.weight)}
            helperText={getFieldError('weight', triageData.weight)}
            InputProps={{
              endAdornment: <InputAdornment position="end">kg</InputAdornment>,
            }}
            inputProps={{ min: 20, max: 300, step: 0.1 }}
          />
        </Grid>
        <Grid size={4}>
          <TextField
            fullWidth
            size="small"
            label="Altura"
            type="number"
            value={triageData.height ?? ''}
            onChange={(e) => handleFieldChange('height', e.target.value)}
            error={!!getFieldError('height', triageData.height)}
            helperText={getFieldError('height', triageData.height)}
            InputProps={{
              endAdornment: <InputAdornment position="end">cm</InputAdornment>,
            }}
            inputProps={{ min: 100, max: 250 }}
          />
        </Grid>
        <Grid size={4}>
          <TextField
            fullWidth
            size="small"
            label="IMC"
            value={bmi !== null ? `${bmi} - ${bmiClassification}` : '-'}
            disabled
            InputProps={{
              readOnly: true,
            }}
          />
        </Grid>

        <Grid size={12}>
          <Divider sx={{ my: 1 }} />
        </Grid>

        {/* Observations */}
        <Grid size={12}>
          <TextField
            fullWidth
            size="small"
            label="Observacoes"
            multiline
            rows={3}
            value={triageData.observations}
            onChange={(e) => handleFieldChange('observations', e.target.value)}
            placeholder="Observacoes adicionais sobre o estado do paciente..."
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default TriageStep;
